import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { addXP } from '../data/xp';
import ModuleSummary from '../components/ModuleSummary';
import { recordStart } from '../data/progress';
import { syncProgress } from '../api/public';

type Screen = 'intro' | 'briefing' | 'containment' | 'disclosure' | 'debrief' | 'final';

interface Action {
  id: string;
  label: string;
  correct: boolean;
  consequence: string;
  timePenalty: number; // seconds lost for wrong action
}

interface DisclosureElement {
  id: string;
  label: string;
  required: boolean;
  explanation: string;
}

interface Scenario {
  id: string;
  title: string;
  breachType: string;
  concept: string;
  situation: string;
  dataAtRisk: string[];
  affectedCount: string;
  actions: Action[];
  correctSequence: string[]; // ordered action IDs
  disclosureElements: DisclosureElement[];
  debrief: string;
  pipedaNote: string;
}

interface ScenarioResult {
  scenarioId: string;
  title: string;
  timeRemaining: number;
  wrongActions: number;
  disclosureScore: number;
  xpEarned: number;
}

const TOTAL_TIME = 300; // 5 minutes per scenario
const XP_COMPLETION = 100;
const MODULE_ID = 'leak-lab';

const SCENARIOS: Scenario[] = [
  {
    id: 'phishing',
    title: 'The Homework Portal',
    breachType: 'Phishing — Credential Theft',
    concept: 'Phishing & Account Compromise',
    situation: 'Northfield Secondary School\'s homework portal has been breached. A teacher clicked a link in a fake "IT support" email and entered their credentials. The attacker now has access to the teacher account — including student assignment submissions, grades, and parent contact details for 340 students.',
    dataAtRisk: ['340 student names and grades', 'Parent email addresses and phone numbers', 'Assignment content including student writing'],
    affectedCount: '340 students',
    correctSequence: ['lock_account', 'reset_passwords', 'scan_access_logs', 'notify_it'],
    actions: [
      { id: 'lock_account', label: 'Immediately lock the compromised teacher account', correct: true, consequence: 'Account locked — attacker access cut off. Clock stopped on ongoing exfiltration.', timePenalty: 0 },
      { id: 'reset_passwords', label: 'Force password reset for all staff accounts', correct: true, consequence: 'All staff credentials invalidated — lateral movement prevented.', timePenalty: 0 },
      { id: 'scan_access_logs', label: 'Pull access logs to determine what data was viewed or downloaded', correct: true, consequence: 'Logs show attacker accessed grades and parent contact list. Scope of breach now known.', timePenalty: 0 },
      { id: 'notify_it', label: 'Notify the school board IT security team', correct: true, consequence: 'IT security engaged. Forensic investigation can begin. Formal incident record created.', timePenalty: 0 },
      { id: 'email_parents', label: 'Email all parents immediately from the breached account', correct: false, consequence: 'Wrong move. The account is still compromised — this could alert the attacker or send from a hijacked address. Lock the account first.', timePenalty: 60 },
      { id: 'delete_logs', label: 'Delete the suspicious email from the teacher\'s inbox to prevent panic', correct: false, consequence: 'Evidence destroyed. Investigators now cannot determine the full scope of the attack. This may also constitute obstruction.', timePenalty: 90 },
    ],
    disclosureElements: [
      { id: 'who', label: 'Who is affected and how many', required: true, explanation: 'Affected individuals must know if their data was exposed.' },
      { id: 'what', label: 'What data was accessed or taken', required: true, explanation: 'People need to know specifically what was compromised to assess their risk.' },
      { id: 'when', label: 'When the breach occurred and when it was discovered', required: true, explanation: 'Timeline helps affected individuals understand the exposure window.' },
      { id: 'action', label: 'What action affected individuals should take', required: true, explanation: 'Practical steps (e.g. watch for suspicious emails) reduce harm.' },
      { id: 'contact', label: 'A contact for questions', required: true, explanation: 'Affected individuals must be able to reach someone with authority.' },
      { id: 'apology', label: 'An apology from the principal', required: false, explanation: 'Appropriate but not legally required under PIPEDA.' },
      { id: 'legal_detail', label: 'Full technical details of the attack vector', required: false, explanation: 'Operational security may prevent full disclosure of attack methodology.' },
    ],
    debrief: 'Phishing is responsible for over 80% of data breaches. The critical first action is always account lockout — every second the compromised account stays active, the attacker can exfiltrate more data. Access logs are essential to scoping the breach: you cannot notify the right people without knowing what was taken.',
    pipedaNote: 'Under PIPEDA, organisations must notify affected individuals and the Office of the Privacy Commissioner "as soon as feasible" after determining a breach poses a real risk of significant harm. There is no fixed 72-hour window in PIPEDA (unlike GDPR) — but delay is not defensible.',
  },
  {
    id: 'misconfigured_bucket',
    title: 'The Open Archive',
    breachType: 'Misconfiguration — Public Cloud Storage',
    concept: 'Cloud Misconfiguration',
    situation: 'A developer at a student tutoring service accidentally made an S3 bucket public while testing a feature. The bucket contains 4 years of session recordings, student progress reports, and payment records for 2,100 users. A security researcher has just emailed to report the bucket is publicly accessible — and has been for 11 days.',
    dataAtRisk: ['Session recordings (audio/video)', '2,100 student progress reports', 'Payment records including partial card numbers', 'Home addresses from billing data'],
    affectedCount: '2,100 users',
    correctSequence: ['make_private', 'audit_bucket', 'check_access_logs', 'preserve_evidence'],
    actions: [
      { id: 'make_private', label: 'Immediately set the S3 bucket back to private', correct: true, consequence: 'Public access revoked. No further exposure from this moment forward.', timePenalty: 0 },
      { id: 'audit_bucket', label: 'Audit all files in the bucket to confirm what was exposed', correct: true, consequence: 'Full scope confirmed: session recordings, reports, and payment records all accessible.', timePenalty: 0 },
      { id: 'check_access_logs', label: 'Review S3 server access logs for external downloads', correct: true, consequence: 'Logs show 47 external IP addresses accessed files. Some downloads confirmed. Breach scope is material.', timePenalty: 0 },
      { id: 'preserve_evidence', label: 'Preserve all logs and configurations before any changes', correct: true, consequence: 'Evidence chain intact. Forensic investigation and regulatory submissions can proceed accurately.', timePenalty: 0 },
      { id: 'thank_researcher', label: 'Thank the researcher and close the email — the bucket is now private', correct: false, consequence: 'The researcher disclosed in good faith. Ignoring them damages trust and may mean you miss additional findings. They should be formally acknowledged.', timePenalty: 30 },
      { id: 'delete_logs', label: 'Delete the S3 access logs to limit liability', correct: false, consequence: 'Destroying evidence during a breach investigation is a serious legal and regulatory violation. This makes things significantly worse.', timePenalty: 120 },
      { id: 'patch_developer', label: 'Immediately terminate the developer who made the error', correct: false, consequence: 'Containment first, investigation second. Acting on personnel before the breach is contained and the facts are established is premature and may create legal exposure.', timePenalty: 45 },
    ],
    disclosureElements: [
      { id: 'who', label: 'Who is affected and how many', required: true, explanation: 'Affected individuals must know if their data was exposed.' },
      { id: 'what', label: 'What data was exposed (specifically)', required: true, explanation: 'Session recordings and payment data carry very different risks — specificity matters.' },
      { id: 'duration', label: 'How long the data was publicly accessible', required: true, explanation: '11 days of exposure is material to the risk assessment individuals must make.' },
      { id: 'action', label: 'What steps affected individuals should take', required: true, explanation: 'Partial card numbers can be used in combination with other data — people need to know.' },
      { id: 'contact', label: 'A contact for further questions', required: true, explanation: 'Required under PIPEDA notification standards.' },
      { id: 'technical_fix', label: 'Detailed explanation of the technical misconfiguration', required: false, explanation: 'Useful context but not a legal requirement in the notification itself.' },
    ],
    debrief: 'Cloud misconfigurations are one of the most common causes of large-scale breaches — and unlike hacks, they are entirely preventable. The 11-day window before discovery is typical: misconfigured buckets are often found by automated scanners before the organisation notices. Responsible disclosure by security researchers should always be handled professionally.',
    pipedaNote: 'Payment card data exposure triggers additional obligations under PCI-DSS. Even partial card numbers combined with names and addresses can enable fraud. PIPEDA\'s "real risk of significant harm" threshold is almost certainly met here given the financial data involved.',
  },
  {
    id: 'insider',
    title: 'The Departing Employee',
    breachType: 'Insider Threat — Data Exfiltration',
    concept: 'Insider Threat',
    situation: 'A sales manager at a health tech startup is leaving for a competitor next week. Your DLP system has flagged that they emailed a 340MB ZIP file to a personal Gmail account two days ago. The file name is "customer_export_final.zip". The file contains contact details, health condition codes, and subscription status for 8,900 customers.',
    dataAtRisk: ['8,900 customer names and contact details', 'Health condition codes (sensitive personal data)', 'Subscription and billing status', 'Sales interaction history'],
    affectedCount: '8,900 customers',
    correctSequence: ['preserve_evidence', 'suspend_access', 'legal_hold', 'hr_involve'],
    actions: [
      { id: 'preserve_evidence', label: 'Preserve the DLP alert, email logs, and file metadata as evidence', correct: true, consequence: 'Evidence chain secured. Any subsequent legal or regulatory action has a solid foundation.', timePenalty: 0 },
      { id: 'suspend_access', label: 'Immediately suspend the employee\'s system access', correct: true, consequence: 'Further exfiltration prevented. Access revoked across all systems.', timePenalty: 0 },
      { id: 'legal_hold', label: 'Place a legal hold on all the employee\'s communications and files', correct: true, consequence: 'Discovery preserved. Employment law and potential regulatory proceedings protected.', timePenalty: 0 },
      { id: 'hr_involve', label: 'Involve HR and legal counsel before confronting the employee', correct: true, consequence: 'Proper process followed. Premature confrontation without HR and legal risks tipping off the employee or creating wrongful termination exposure.', timePenalty: 0 },
      { id: 'confront_employee', label: 'Call the employee in immediately and demand they delete the file', correct: false, consequence: 'Without HR and legal present, this creates liability and gives the employee an opportunity to deny or destroy additional evidence. Process matters.', timePenalty: 75 },
      { id: 'ignore_flag', label: 'Wait — the employee is leaving anyway, the problem will resolve itself', correct: false, consequence: 'The data is already at the competitor\'s email. Waiting does not contain the breach — it makes the organisation liable for failing to act on a known risk.', timePenalty: 120 },
      { id: 'email_employee', label: 'Email the employee asking them to confirm what is in the ZIP file', correct: false, consequence: 'Alerting the subject of an investigation before evidence is secured is an investigative error. They may delete or forward further copies.', timePenalty: 90 },
    ],
    disclosureElements: [
      { id: 'who', label: 'Who is affected', required: true, explanation: 'Customers with health condition codes have the right to know their sensitive data was exfiltrated.' },
      { id: 'what', label: 'What data was taken (including health codes)', required: true, explanation: 'Health data is a sensitive category — its exposure must be explicitly named.' },
      { id: 'action', label: 'Recommended action for affected individuals', required: true, explanation: 'With health data involved, individuals may need to monitor for identity fraud or targeted scams.' },
      { id: 'contact', label: 'A contact person for questions', required: true, explanation: 'Required under PIPEDA notification.' },
      { id: 'when', label: 'When the exfiltration occurred', required: true, explanation: 'Two days of exposure before detection is material to individual risk assessment.' },
      { id: 'employee_name', label: 'The name of the employee who took the data', required: false, explanation: 'Not required in public notification — may be relevant in regulatory submissions but naming individuals creates additional legal risk.' },
    ],
    debrief: 'Insider threats are among the hardest breaches to detect because the activity looks like normal work. DLP (Data Loss Prevention) systems flag anomalous transfers, but they are only useful if someone reviews the alerts. The key principle: treat insider breaches with the same urgency as external attacks, and always involve HR and legal before confronting the subject.',
    pipedaNote: 'Health condition codes are sensitive personal data under PIPEDA. Their exfiltration almost certainly meets the "real risk of significant harm" threshold, triggering mandatory notification to both the OPC and affected individuals.',
  },
  {
    id: 'unpatched',
    title: 'The Old Vulnerability',
    breachType: 'Unpatched Software — Remote Code Execution',
    concept: 'Patch Management',
    situation: 'Attackers exploited a CVE published 4 months ago in the school district\'s student information system. A patch has been available since the CVE was published — but it was never applied. The attackers had access for approximately 72 hours. Records for 14,000 students across 12 schools are potentially compromised: names, dates of birth, home addresses, guardian contact details, and special education designations.',
    dataAtRisk: ['14,000 student names and dates of birth', 'Home addresses', 'Guardian names and contact details', 'Special education designations (sensitive)'],
    affectedCount: '14,000 students across 12 schools',
    correctSequence: ['isolate_system', 'apply_patch', 'forensic_image', 'notify_opc'],
    actions: [
      { id: 'isolate_system', label: 'Immediately isolate the affected student information system from the network', correct: true, consequence: 'System isolated. Attacker\'s active connection severed. No further data can be accessed.', timePenalty: 0 },
      { id: 'apply_patch', label: 'Apply the pending security patch before bringing the system back online', correct: true, consequence: 'Vulnerability closed. The system cannot be recompromised via the same vector.', timePenalty: 0 },
      { id: 'forensic_image', label: 'Take a forensic image of the system before any changes', correct: true, consequence: 'Evidence preserved. The 72-hour access window and attacker activity can be reconstructed accurately.', timePenalty: 0 },
      { id: 'notify_opc', label: 'Notify the Office of the Privacy Commissioner', correct: true, consequence: 'Regulatory obligation triggered. OPC notified within the required timeframe.', timePenalty: 0 },
      { id: 'patch_later', label: 'Apply the patch next maintenance window to avoid disrupting report card season', correct: false, consequence: 'The system is actively compromised. Delaying the patch to avoid inconvenience leaves students\' data exposed. Operational continuity does not override breach containment.', timePenalty: 120 },
      { id: 'blame_vendor', label: 'Issue a statement blaming the software vendor for the vulnerability', correct: false, consequence: 'The CVE was public for 4 months with a patch available. Blame-shifting is not a containment action and will not be credible to regulators or the public.', timePenalty: 60 },
      { id: 'wipe_system', label: 'Wipe and rebuild the server immediately', correct: false, consequence: 'Without a forensic image first, all evidence of the attack — attacker IP, methods, and scope of access — is destroyed. Forensic image always comes before remediation.', timePenalty: 90 },
    ],
    disclosureElements: [
      { id: 'who', label: 'Which schools and how many students are affected', required: true, explanation: 'With 12 schools involved, guardians need to know if their child\'s school is affected.' },
      { id: 'what', label: 'What data was exposed, including special education designations', required: true, explanation: 'Special education designations are sensitive — their exposure must be explicitly named.' },
      { id: 'when', label: 'How long the attacker had access', required: true, explanation: '72-hour access window is material to assessing the scope of what may have been taken.' },
      { id: 'action', label: 'Steps guardians should take', required: true, explanation: 'Home addresses and dates of birth together enable identity fraud — guardians need to know the risk.' },
      { id: 'contact', label: 'District privacy officer contact details', required: true, explanation: 'Required under PIPEDA notification and expected by affected families.' },
      { id: 'patch_history', label: 'Why the patch was not applied sooner', required: false, explanation: 'Relevant to accountability but not legally required in the notification itself — more appropriate for the regulatory submission.' },
    ],
    debrief: 'Unpatched vulnerabilities are the leading cause of preventable breaches. A CVE is a public announcement that a vulnerability exists and attackers are already looking for unpatched systems. The 4-month gap between patch availability and application is a governance failure, not a technical one. Patch management requires policy, ownership, and enforcement — not just awareness.',
    pipedaNote: 'Children\'s data and special education designations represent sensitive categories under Canadian privacy law. The scale (14,000 individuals) and sensitivity of the data almost certainly require notification to both the OPC and all affected guardians.',
  },
  {
    id: 'ransomware',
    title: 'The Friday Night Lock',
    breachType: 'Ransomware — Encrypted Systems',
    concept: 'Ransomware Response',
    situation: 'At 11:47 PM on a Friday, the city library system\'s network goes down. By midnight, ransomware notes appear on all workstations: "Your files are encrypted. Pay 3 BTC within 48 hours or data will be published." The ransomware group has a known history of publishing stolen data. Library servers hold borrowing records (books checked out) for 67,000 library card holders — a privacy-sensitive record given that reading habits reveal religious, political, and health interests.',
    dataAtRisk: ['67,000 borrowing records (privacy-sensitive)', 'Library card holder names, addresses, email addresses', 'Digital resource access logs (e-books, databases)'],
    affectedCount: '67,000 library card holders',
    correctSequence: ['isolate_network', 'do_not_pay', 'contact_cisa', 'restore_backup'],
    actions: [
      { id: 'isolate_network', label: 'Immediately isolate all affected systems from the network', correct: true, consequence: 'Ransomware spread contained. Systems that have not yet been encrypted are protected.', timePenalty: 0 },
      { id: 'do_not_pay', label: 'Do not pay the ransom — document this decision with rationale', correct: true, consequence: 'Correct. Payment funds criminal operations, does not guarantee recovery, and may violate sanctions law if the group is designated. Document the decision for regulators.', timePenalty: 0 },
      { id: 'contact_cisa', label: 'Contact the Canadian Centre for Cyber Security (CCCS) to report the incident', correct: true, consequence: 'CCCS engaged. They have decryptors for some known ransomware variants and can provide forensic support. This is the right call.', timePenalty: 0 },
      { id: 'restore_backup', label: 'Identify and verify offline backups, then begin restoration from clean snapshot', correct: true, consequence: 'Backups intact and unencrypted. Recovery can begin without paying. System will be restored to the state before infection.', timePenalty: 0 },
      { id: 'pay_ransom', label: 'Pay the 3 BTC ransom to recover the files quickly', correct: false, consequence: 'Payment funds criminal infrastructure, provides no guarantee of recovery, and may violate OFAC sanctions if the group is designated. 40% of organisations that pay never recover their data anyway.', timePenalty: 150 },
      { id: 'negotiate', label: 'Open negotiations with the ransomware group to buy time', correct: false, consequence: 'Negotiating without law enforcement involvement and without a clear strategy extends the incident window and signals willingness to pay. Contact CCCS first.', timePenalty: 60 },
      { id: 'wipe_restore', label: 'Wipe all affected systems immediately and restore from last backup', correct: false, consequence: 'Without isolating first and verifying the backup is clean, you may be restoring from an already-infected snapshot — or allowing the ransomware to spread during restoration.', timePenalty: 90 },
    ],
    disclosureElements: [
      { id: 'who', label: 'Who is affected — all library card holders', required: true, explanation: 'All 67,000 card holders have borrowing records at risk.' },
      { id: 'what', label: 'What data is at risk, specifically borrowing records', required: true, explanation: 'Borrowing records reveal reading habits — religious, health, and political interests. Their sensitivity must be named explicitly.' },
      { id: 'publication_risk', label: 'The risk that data may be published by the attackers', required: true, explanation: 'This ransomware group has a known history of publishing data. Affected individuals need to know this risk is real.' },
      { id: 'action', label: 'What card holders should watch for', required: true, explanation: 'With addresses and emails exposed, phishing attempts targeting card holders are foreseeable.' },
      { id: 'contact', label: 'Library privacy contact and escalation path', required: true, explanation: 'Required under PIPEDA. Card holders must be able to reach someone.' },
      { id: 'ransom_amount', label: 'The ransom amount demanded', required: false, explanation: 'Not legally required in the notification and may complicate law enforcement proceedings.' },
    ],
    debrief: 'Borrowing records are among the most privacy-sensitive records a public institution holds — they reveal what people read, which correlates with religious belief, health concerns, and political views. Libraries have historically fought subpoenas for this reason. Ransomware attackers increasingly target public institutions because they are less likely to have mature security practices. The most important preparation is offline, tested backups.',
    pipedaNote: 'The threat to publish data means the breach poses a "real risk of significant harm" even if the data has not yet been published. PIPEDA notification obligations are triggered at the point the risk is identified — not when harm occurs.',
  },
];

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function calcXP(timeRemaining: number, wrongActions: number, disclosureScore: number): number {
  const timeBonus = Math.round((timeRemaining / TOTAL_TIME) * 25);
  const accuracyBonus = Math.max(0, 20 - wrongActions * 8);
  const disclosureBonus = Math.round(disclosureScore * 0.05);
  return Math.min(50, timeBonus + accuracyBonus + disclosureBonus);
}

export default function LeakLab() {
  const navigate = useNavigate();
  const [screen, setScreen] = useState<Screen>('intro');
  const [scenarioIndex, setScenarioIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(TOTAL_TIME);
  const [timerActive, setTimerActive] = useState(false);
  const [completedActions, setCompletedActions] = useState<string[]>([]);
  const [wrongActions, setWrongActions] = useState(0);
  const [lastFeedback, setLastFeedback] = useState<{ action: Action; correct: boolean } | null>(null);
  const [disclosureSelected, setDisclosureSelected] = useState<string[]>([]);
  const [disclosureSubmitted, setDisclosureSubmitted] = useState(false);
  const [results, setResults] = useState<ScenarioResult[]>([]);
  const [sessionXP, setSessionXP] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const scenario = SCENARIOS[scenarioIndex];
  const correctSequence = scenario?.correctSequence ?? [];
  const remainingCorrect = correctSequence.filter(id => !completedActions.includes(id));
  const nextCorrectAction = remainingCorrect[0];
  const allActionsComplete = remainingCorrect.length === 0;

  useEffect(() => {
    if (timerActive && timeRemaining > 0) {
      timerRef.current = setInterval(() => setTimeRemaining(t => t - 1), 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [timerActive, timeRemaining]);

  function startScenario() {
    setTimeRemaining(TOTAL_TIME);
    setCompletedActions([]);
    setWrongActions(0);
    setLastFeedback(null);
    setDisclosureSelected([]);
    setDisclosureSubmitted(false);
    setTimerActive(true);
    setScreen('containment');
  }

  function handleAction(action: Action) {
    if (lastFeedback !== null) return;
    setLastFeedback({ action, correct: action.correct });
    if (action.correct) {
      setCompletedActions(prev => [...prev, action.id]);
    } else {
      setWrongActions(w => w + 1);
      setTimeRemaining(t => Math.max(0, t - action.timePenalty));
    }
  }

  function dismissFeedback() {
    setLastFeedback(null);
    if (allActionsComplete || (lastFeedback?.correct && remainingCorrect.length === 1)) {
      setTimerActive(false);
      setScreen('disclosure');
    }
  }

  function toggleDisclosure(id: string) {
    setDisclosureSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  function submitDisclosure() {
    setDisclosureSubmitted(true);
    setTimerActive(false);
    const required = scenario.disclosureElements.filter(e => e.required).map(e => e.id);
    const included = required.filter(id => disclosureSelected.includes(id));
    const disclosureScore = Math.round((included.length / required.length) * 100);
    const xp = calcXP(timeRemaining, wrongActions, disclosureScore);
    addXP(xp);
    setSessionXP(prev => prev + xp);
    setResults(prev => [...prev, {
      scenarioId: scenario.id,
      title: scenario.title,
      timeRemaining,
      wrongActions,
      disclosureScore,
      xpEarned: xp,
    }]);
    setScreen('debrief');
  }

  function handleNextScenario() {
    if (scenarioIndex < SCENARIOS.length - 1) {
      setScenarioIndex(i => i + 1);
      setScreen('briefing');
    } else {
      addXP(XP_COMPLETION, MODULE_ID);
      recordStart();
      const total = sessionXP + XP_COMPLETION;
      setSessionXP(total);
      syncProgress(MODULE_ID, total);
      setScreen('final');
    }
  }

  const lastResult = results[results.length - 1];
  const overallXP = results.reduce((s, r) => s + r.xpEarned, 0);
  const avgDisclosure = results.length > 0 ? Math.round(results.reduce((s, r) => s + r.disclosureScore, 0) / results.length) : 0;

  const timerColor = timeRemaining > 120 ? '#10b981' : timeRemaining > 60 ? '#f59e0b' : '#ef4444';

  return (
    <div style={{ minHeight: '100vh', background: '#08090f', color: '#e2e8f0' }}>
      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.5; } }
        @keyframes timerPulse { 0%,100% { transform:scale(1); } 50% { transform:scale(1.04); } }
      `}</style>
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '20px 16px 48px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 10, color: '#ef4444', letterSpacing: '0.15em', textTransform: 'uppercase' as const }}>ESCAPE ROOM · INCIDENT RESPONSE</div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#f8fafc', lineHeight: 1.2, marginTop: 2 }}>The Leak Lab</h1>
          </div>
          <div style={{ textAlign: 'right' as const }}>
            {screen === 'containment' && (
              <div style={{ fontSize: 28, fontWeight: 800, color: timerColor, fontVariantNumeric: 'tabular-nums', animation: timeRemaining <= 30 ? 'timerPulse 0.8s infinite' : 'none' }}>
                {formatTime(timeRemaining)}
              </div>
            )}
            <div style={{ fontSize: 11, color: '#ffa502', marginTop: 2 }}>+{sessionXP} XP</div>
          </div>
        </div>

        {/* ── Intro ── */}
        {screen === 'intro' && (
          <div style={{ animation: 'fadeIn 0.5s ease' }}>
            <div style={{ background: 'linear-gradient(135deg,rgba(239,68,68,0.08),rgba(220,38,38,0.04))', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 16, padding: '28px 24px', marginBottom: 24 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', marginBottom: 12 }}>You are the Incident Response Lead</h2>
              <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.7, marginBottom: 10 }}>
                Five data breaches have hit five different organisations. In each scenario, you have a limited time to contain the breach by selecting the right actions in the right order.
              </p>
              <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.7, marginBottom: 10 }}>
                Wrong moves cost time. After containment, you will draft the breach disclosure notification — what you tell affected individuals matters as much as how fast you act.
              </p>
              <p style={{ fontSize: 14, color: '#ef4444', lineHeight: 1.7 }}>
                You have 5 minutes per breach. The clock starts when you enter the scenario.
              </p>
            </div>

            <div style={{ background: 'rgba(8,9,15,0.8)', border: '1px solid #1e293b', borderRadius: 12, padding: '16px 20px', marginBottom: 24 }}>
              <div style={{ fontSize: 11, color: '#475569', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 12 }}>Scoring</div>
              {[
                { label: 'Per scenario — based on time and accuracy', xp: 'Up to 50 XP' },
                { label: 'Correct disclosure notification', xp: 'Included in score' },
                { label: 'Module completion bonus', xp: '+100 XP' },
              ].map((s, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: i < 2 ? '1px solid #1e293b' : 'none' }}>
                  <span style={{ fontSize: 13, color: '#cbd5e1' }}>{s.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#ffa502' }}>{s.xp}</span>
                </div>
              ))}
            </div>

            <button onClick={() => setScreen('briefing')} style={{ width: '100%', padding: 16, borderRadius: 12, background: 'linear-gradient(135deg,#dc2626,#b91c1c)', border: 'none', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
              Enter the Lab
            </button>
            <button onClick={() => navigate('/')} style={{ width: '100%', marginTop: 10, padding: 12, background: 'none', border: '1px solid #1e293b', borderRadius: 10, color: '#475569', fontSize: 14, cursor: 'pointer' }}>
              Back to Home
            </button>
          </div>
        )}

        {/* ── Briefing ── */}
        {screen === 'briefing' && (
          <div style={{ animation: 'fadeIn 0.4s ease' }}>
            <div style={{ background: '#0f1117', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 16, padding: '24px 22px', marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 10, color: '#ef4444', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.12em' }}>
                    Scenario {scenarioIndex + 1} of {SCENARIOS.length} — {scenario.breachType}
                  </div>
                  <h2 style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', marginTop: 6 }}>{scenario.title}</h2>
                </div>
                <div style={{ fontSize: 11, color: '#64748b', textAlign: 'right' as const }}>Concept:<br /><span style={{ color: '#94a3b8', fontWeight: 600 }}>{scenario.concept}</span></div>
              </div>

              <div style={{ background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: '#ef4444', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 8 }}>Situation Report</div>
                <p style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.7, margin: 0 }}>{scenario.situation}</p>
              </div>

              <div style={{ marginBottom: 0 }}>
                <div style={{ fontSize: 11, color: '#475569', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 8 }}>Data at Risk</div>
                {scenario.dataAtRisk.map((d, i) => (
                  <div key={i} style={{ fontSize: 13, color: '#fca5a5', lineHeight: 1.6, marginBottom: 3 }}>· {d}</div>
                ))}
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>Affected: <span style={{ color: '#ef4444', fontWeight: 700 }}>{scenario.affectedCount}</span></div>
              </div>
            </div>

            <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 24, fontWeight: 900, color: '#ef4444', minWidth: 48, textAlign: 'center' as const }}>{formatTime(TOTAL_TIME)}</div>
              <div style={{ fontSize: 13, color: '#94a3b8' }}>Your time starts the moment you begin. Wrong actions cost time. Correct actions stop the breach.</div>
            </div>

            <button onClick={startScenario} style={{ width: '100%', padding: 14, borderRadius: 12, background: 'linear-gradient(135deg,#dc2626,#b91c1c)', border: 'none', color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              Start Response — Clock Running
            </button>
          </div>
        )}

        {/* ── Containment ── */}
        {screen === 'containment' && (
          <div style={{ animation: 'fadeIn 0.3s ease' }}>
            {/* Progress */}
            <div style={{ background: '#0f1117', border: '1px solid #1e293b', borderRadius: 10, padding: '10px 16px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#ef4444', fontWeight: 700 }}>{scenario.title}</span>
              <div style={{ display: 'flex', gap: 4 }}>
                {correctSequence.map(id => (
                  <div key={id} style={{ width: 8, height: 8, borderRadius: '50%', background: completedActions.includes(id) ? '#10b981' : '#1e293b', border: completedActions.includes(id) ? 'none' : '1px solid #334155' }} />
                ))}
              </div>
            </div>

            {/* Feedback overlay */}
            {lastFeedback && (
              <div style={{ animation: 'fadeIn 0.3s ease', background: lastFeedback.correct ? 'rgba(16,185,129,0.06)' : 'rgba(239,68,68,0.06)', border: `1px solid ${lastFeedback.correct ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`, borderRadius: 14, padding: '18px 20px', marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 8, color: lastFeedback.correct ? '#10b981' : '#ef4444' }}>
                  {lastFeedback.correct ? 'Correct action' : `Wrong action — ${lastFeedback.action.timePenalty}s lost`}
                </div>
                <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.6, marginBottom: 14 }}>{lastFeedback.action.consequence}</div>
                <button onClick={dismissFeedback} style={{ padding: '9px 20px', borderRadius: 8, background: lastFeedback.correct ? '#10b981' : '#ef4444', border: 'none', color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  {allActionsComplete || (lastFeedback.correct && remainingCorrect.length === 1) ? 'Breach Contained — Draft Disclosure' : 'Continue'}
                </button>
              </div>
            )}

            {/* Action cards */}
            {!lastFeedback && (
              <div>
                <div style={{ fontSize: 11, color: '#475569', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 12 }}>
                  Select your next action — {completedActions.length}/{correctSequence.length} steps complete
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 10 }}>
                  {scenario.actions.map(action => {
                    const done = completedActions.includes(action.id);
                    return (
                      <button
                        key={action.id}
                        onClick={() => !done && handleAction(action)}
                        disabled={done}
                        style={{
                          padding: '14px 18px', borderRadius: 12, textAlign: 'left' as const,
                          cursor: done ? 'default' : 'pointer',
                          background: done ? 'rgba(16,185,129,0.06)' : 'rgba(255,255,255,0.02)',
                          border: done ? '1.5px solid rgba(16,185,129,0.3)' : '1.5px solid #1e293b',
                          color: done ? '#10b981' : '#cbd5e1',
                          fontSize: 13, fontWeight: done ? 600 : 400, lineHeight: 1.5,
                          transition: 'all 0.15s',
                          opacity: done ? 0.7 : 1,
                        }}
                      >
                        {done && <span style={{ marginRight: 8 }}>✓</span>}
                        {action.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Disclosure ── */}
        {screen === 'disclosure' && (
          <div style={{ animation: 'fadeIn 0.4s ease' }}>
            <div style={{ background: '#0f1117', border: '1px solid #1e293b', borderRadius: 14, padding: '20px 22px', marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: '#a78bfa', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 6 }}>Breach Contained</div>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', marginBottom: 4 }}>Draft the Disclosure Notification</h2>
              <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
                Under PIPEDA, you must notify affected individuals. Select every element that must be included in this notification.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 10, marginBottom: 20 }}>
              {scenario.disclosureElements.map(el => {
                const selected = disclosureSelected.includes(el.id);
                return (
                  <button
                    key={el.id}
                    onClick={() => !disclosureSubmitted && toggleDisclosure(el.id)}
                    style={{
                      padding: '14px 16px', borderRadius: 12, textAlign: 'left' as const, cursor: disclosureSubmitted ? 'default' : 'pointer',
                      background: selected ? 'rgba(99,102,241,0.08)' : 'rgba(255,255,255,0.02)',
                      border: `1.5px solid ${selected ? 'rgba(99,102,241,0.4)' : '#1e293b'}`,
                      color: selected ? '#a5b4fc' : '#94a3b8',
                      fontSize: 13, lineHeight: 1.5, transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${selected ? '#6366f1' : '#334155'}`, background: selected ? '#6366f1' : 'transparent', flexShrink: 0, marginTop: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {selected && <span style={{ fontSize: 10, color: 'white', fontWeight: 900 }}>✓</span>}
                      </div>
                      {el.label}
                    </div>
                  </button>
                );
              })}
            </div>

            {!disclosureSubmitted && (
              <button onClick={submitDisclosure} style={{ width: '100%', padding: 14, borderRadius: 12, background: 'linear-gradient(135deg,#6366f1,#7c3aed)', border: 'none', color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                Submit Notification
              </button>
            )}
          </div>
        )}

        {/* ── Debrief ── */}
        {screen === 'debrief' && lastResult && (
          <div style={{ animation: 'fadeIn 0.5s ease' }}>
            <div style={{ textAlign: 'center' as const, marginBottom: 24 }}>
              <div style={{ fontSize: 11, color: '#ef4444', letterSpacing: '0.12em', textTransform: 'uppercase' as const, marginBottom: 6 }}>Scenario Complete</div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9' }}>{lastResult.title}</h2>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 20 }}>
              {[
                { label: 'Time Left', value: formatTime(lastResult.timeRemaining), color: lastResult.timeRemaining > 120 ? '#10b981' : lastResult.timeRemaining > 60 ? '#f59e0b' : '#ef4444' },
                { label: 'Wrong Moves', value: String(lastResult.wrongActions), color: lastResult.wrongActions === 0 ? '#10b981' : lastResult.wrongActions <= 2 ? '#f59e0b' : '#ef4444' },
                { label: 'Disclosure', value: `${lastResult.disclosureScore}%`, color: lastResult.disclosureScore >= 80 ? '#10b981' : lastResult.disclosureScore >= 60 ? '#f59e0b' : '#ef4444' },
              ].map(stat => (
                <div key={stat.label} style={{ background: '#0f1117', border: '1px solid #1e293b', borderRadius: 12, padding: '14px 10px', textAlign: 'center' as const }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: stat.color }}>{stat.value}</div>
                  <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{stat.label}</div>
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'center' as const, marginBottom: 20 }}>
              <span style={{ fontSize: 14, color: '#ffa502', fontWeight: 700 }}>+{lastResult.xpEarned} XP earned</span>
            </div>

            {/* Disclosure review */}
            <div style={{ background: '#0f1117', border: '1px solid #1e293b', borderRadius: 12, padding: '16px 18px', marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: '#475569', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 12 }}>Disclosure Review</div>
              {scenario.disclosureElements.map(el => {
                const selected = disclosureSelected.includes(el.id);
                const correct = el.required ? selected : true;
                const wrongInclude = !el.required && selected;
                return (
                  <div key={el.id} style={{ padding: '8px 0', borderBottom: '1px solid #0f172a' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ fontSize: 13, color: correct && !wrongInclude ? '#cbd5e1' : '#94a3b8', flex: 1 }}>{el.label}</div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: el.required ? (selected ? '#10b981' : '#ef4444') : (selected ? '#f59e0b' : '#64748b'), minWidth: 60, textAlign: 'right' as const }}>
                        {el.required ? (selected ? 'Included' : 'Missing') : (selected ? 'Not required' : 'Skipped')}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: '#475569', marginTop: 3, lineHeight: 1.5 }}>{el.explanation}</div>
                  </div>
                );
              })}
            </div>

            {/* Debrief and PIPEDA note */}
            <div style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)', borderRadius: 12, padding: '16px 18px', marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: '#818cf8', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 8 }}>Debrief — {scenario.concept}</div>
              <p style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.7, margin: 0 }}>{scenario.debrief}</p>
            </div>
            <div style={{ background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.15)', borderRadius: 12, padding: '14px 18px', marginBottom: 24 }}>
              <div style={{ fontSize: 11, color: '#a78bfa', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 6 }}>PIPEDA Context</div>
              <p style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.7, margin: 0 }}>{scenario.pipedaNote}</p>
            </div>

            <button onClick={handleNextScenario} style={{ width: '100%', padding: 14, borderRadius: 12, background: 'linear-gradient(135deg,#dc2626,#b91c1c)', border: 'none', color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              {scenarioIndex < SCENARIOS.length - 1 ? `Next Breach: ${SCENARIOS[scenarioIndex + 1].title}` : 'See Final Results'}
            </button>
          </div>
        )}

        {/* ── Final ── */}
        {screen === 'final' && (
          <div style={{ animation: 'fadeIn 0.6s ease' }}>
            <div style={{ textAlign: 'center' as const, marginBottom: 28 }}>
              <div style={{ fontSize: 11, color: '#ef4444', letterSpacing: '0.15em', textTransform: 'uppercase' as const, fontWeight: 700, marginBottom: 8 }}>All Breaches Contained</div>
              <h2 style={{ fontSize: 24, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>Incident Response Complete</h2>
            </div>

            <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 16, padding: '24px 20px', textAlign: 'center' as const, marginBottom: 24 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 32, fontWeight: 800, color: '#ffa502' }}>{overallXP + XP_COMPLETION}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>Total XP earned</div>
                </div>
                <div>
                  <div style={{ fontSize: 32, fontWeight: 800, color: avgDisclosure >= 80 ? '#10b981' : avgDisclosure >= 60 ? '#f59e0b' : '#ef4444' }}>{avgDisclosure}%</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>Avg disclosure score</div>
                </div>
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#ffa502' }}>+{sessionXP} XP total</div>
            </div>

            <div style={{ background: '#0f1117', border: '1px solid #1e293b', borderRadius: 12, padding: '16px 18px', marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: '#475569', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 12 }}>Breach Breakdown</div>
              {results.map((r, i) => (
                <div key={r.scenarioId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: i < results.length - 1 ? '1px solid #0f172a' : 'none' }}>
                  <div style={{ fontSize: 13, color: '#cbd5e1', flex: 1 }}>{r.title}</div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: '#64748b' }}>{formatTime(r.timeRemaining)} left</span>
                    <span style={{ fontSize: 12, color: r.wrongActions === 0 ? '#10b981' : '#f59e0b' }}>{r.wrongActions} wrong</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#ffa502' }}>+{r.xpEarned}</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 12, padding: '14px 18px', marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#818cf8', marginBottom: 4 }}>Badge Earned: Incident Commander</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>For containing five data breaches and learning PIPEDA notification requirements under pressure.</div>
            </div>

            <ModuleSummary moduleId="leaklab" xpEarned={sessionXP} score={avgDisclosure} scoreLabel="Avg Disclosure Score" />
          </div>
        )}
      </div>
    </div>
  );
}
